import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { getOptionSettings } from '../../lib/api/booking' // ensure tree-shake friendliness; not used directly
import { useBooking } from '../../store/bookingStore'
import { useAuth } from '../../store/authStore'
import { useCalculatedPrice } from '../../lib/useCalculatedPrice'
import { StepShell } from '../../components/layout/StepShell'
import { Field, Checkbox } from '../../components/ui/Field'
import { Select } from '../../components/ui/Select'
import { COUNTRIES } from '../../lib/countries'
import { stepPath } from '../../lib/steps'
import type { BookingInfo } from '../../lib/api/types'
import { BillingFields, billingValid, billingTypeOf } from '../billing/BillingFields'
import { BILLING_ENABLED } from '../../lib/platform'

void getOptionSettings

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({ value: c.code, label: c.name }))

export function InfoStep() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { locale: routeLocale = 'it' } = useParams()

  const cart = useBooking((s) => s.cart)
  const info = useBooking((s) => s.bookingInfo)
  const setBookingInfo = useBooking((s) => s.setBookingInfo)
  const clearStaleDates = useBooking((s) => s.clearStaleDates)
  const user = useAuth((s) => s.user)
  const status = useAuth((s) => s.status)
  useCalculatedPrice()

  useEffect(() => {
    if (clearStaleDates() || !cart.transportId || !cart.fromDay || !cart.fromHour || !cart.toDay)
      navigate(stepPath(routeLocale, 'dates'), { replace: true })
  }, [clearStaleDates, cart.transportId, cart.fromDay, cart.fromHour, cart.toDay, navigate, routeLocale])

  // Prefill personal + invoicing fields from the member profile (only empty fields, so typed values
  // win). Fattura is always required, so we also default the billing type (azienda if the profile
  // already holds company data, else privato).
  useEffect(() => {
    const cur = useBooking.getState().bookingInfo
    const looksLikeEmail = (s: string) => /@/.test(s)
    const patch: Partial<BookingInfo> = {}
    // a stale persisted name that's actually an email → use the real account name or clear it
    if (cur.name && looksLikeEmail(cur.name)) patch.name = user?.name && !looksLikeEmail(user.name) ? user.name : ''
    if (user) {
      if (!cur.name && user.name && !looksLikeEmail(user.name)) patch.name = user.name
      if (!cur.lastname && user.lastname) patch.lastname = user.lastname
      if (!cur.email && user.email) patch.email = user.email
      if (!cur.phoneNumber && user.phoneNumber) patch.phoneNumber = user.phoneNumber
      if (!cur.licensePlate && user.licensePlate) patch.licensePlate = user.licensePlate
      if (!cur.country && user.country) patch.country = user.country
      if (!cur.codiceFiscale && user.codiceFiscale) patch.codiceFiscale = user.codiceFiscale
      if (!cur.referente && user.referente) patch.referente = user.referente
      if (!cur.businessName && user.businessName) patch.businessName = user.businessName
      if (!cur.businessVatId && user.businessVatId) patch.businessVatId = user.businessVatId
      if (!cur.businessSDI && user.businessSDI) patch.businessSDI = user.businessSDI
      if (!cur.businessPec && user.businessPec) patch.businessPec = user.businessPec
      if (!cur.businessAddress && user.businessAddress) patch.businessAddress = user.businessAddress
      if (!cur.businessHouseNumber && user.businessHouseNumber) patch.businessHouseNumber = user.businessHouseNumber
      if (!cur.businessCity && user.businessCity) patch.businessCity = user.businessCity
      if (!cur.businessZipCode && user.businessZipCode) patch.businessZipCode = user.businessZipCode
      if (!cur.businessRegionProvince && user.businessRegionProvince) patch.businessRegionProvince = user.businessRegionProvince
    }
    if (!cur.billingType)
      patch.billingType = user?.billingType || (user?.businessName || user?.businessVatId ? 'azienda' : 'privato')
    // Billing flag: when fatturazione is enabled, force it on so the backend attaches billing data;
    // when hidden, clear it so bookings go through without an invoice (backend then sends fattura="0").
    if (BILLING_ENABLED) {
      if (cur.requireBilling !== 'true') patch.requireBilling = 'true'
    } else if (cur.requireBilling === 'true') {
      patch.requireBilling = ''
    }
    // Single source of truth for country: the contact "Nazione" drives the billing country (mirror).
    const country = patch.country ?? cur.country ?? user?.country ?? 'IT'
    if (cur.country !== country) patch.country = country
    if (cur.businessCountry !== country) patch.businessCountry = country
    if (Object.keys(patch).length) setBookingInfo(patch)
  }, [user, setBookingInfo])

  // Prefill the Azienda billing email from the contact email ("I tuoi dati") until the user edits it.
  const billingEmailEdited = useRef(false)
  useEffect(() => {
    if (!billingEmailEdited.current && (info.businessEmail ?? '') !== info.email) {
      setBookingInfo({ businessEmail: info.email })
    }
  }, [info.email, info.businessEmail, setBookingInfo])

  const [touched, setTouched] = useState(false)

  // Email confirmation — LOCAL ONLY: never written to bookingInfo, so it never reaches the
  // backend (one email is enough server-side). It only guards against typos at entry time.
  const [confirmEmail, setConfirmEmail] = useState('')
  const confirmSeeded = useRef(false)
  useEffect(() => {
    // signed-in users have a trusted, prefilled email → seed the confirm so they don't retype;
    // guests typing manually must still re-enter it.
    if (!confirmSeeded.current && status === 'authenticated' && info.email) {
      setConfirmEmail(info.email)
      confirmSeeded.current = true
    }
  }, [status, info.email])

  function set<K extends keyof BookingInfo>(key: K, value: BookingInfo[K]) {
    setBookingInfo({ [key]: value } as Partial<BookingInfo>)
  }

  // For Azienda the company (Ragione sociale) is the invoice holder, so personal Nome/Cognome
  // aren't required — they're derived from the company name on continue (the backend always
  // needs a non-empty name for the reservation customer).
  const isAzienda = BILLING_ENABLED && billingTypeOf(info) === 'azienda'
  const errors = {
    name: !isAzienda && !info.name.trim(),
    lastname: !isAzienda && !info.lastname.trim(),
    email: !EMAIL_RE.test(info.email),
    emailConfirm: info.email.trim() !== confirmEmail.trim(),
    phoneNumber: !info.phoneNumber.trim(),
    privacy01: info.privacy01 !== 'true',
  }
  const valid = !Object.values(errors).some(Boolean) && (!BILLING_ENABLED || billingValid(info))

  function scrollToFirstError() {
    setTimeout(() => {
      const el = document.querySelector('[aria-invalid="true"]') as HTMLElement | null
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        el.focus({ preventScroll: true })
      }
    }, 60)
  }

  function onContinue() {
    setTouched(true)
    if (!valid) {
      scrollToFirstError()
      return
    }
    if (isAzienda && !info.name.trim()) {
      // company booking: use the Ragione sociale as the reservation holder name
      setBookingInfo({ name: info.businessName ?? '', lastname: info.lastname ?? '' })
    }
    navigate(stepPath(routeLocale, 'payment'))
  }

  const err = (k: keyof typeof errors) => (touched && errors[k] ? t('common.required') : undefined)

  return (
    <StepShell
      stepKey="info"
      title={t('info.title')}
      subtitle={t('info.subtitle')}
      onBack={() => navigate(stepPath(routeLocale, 'rate'))}
      onPrimary={onContinue}
      primaryDisabled={touched && !valid}
    >
      {status !== 'authenticated' && (
        <div className="gsm-card mb-4 p-5 sm:p-6">
          {/* Login/register to prefill — returns to THIS exact step after sign-in; the booking
              (cart + entered fields) is persisted to localStorage, so nothing is lost. */}
          <button
            type="button"
            onClick={() => navigate(`/${routeLocale}/login?redirect=${encodeURIComponent(stepPath(routeLocale, 'info'))}`)}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 py-3.5 text-base font-bold text-white transition hover:bg-brand-700"
          >
            {t('info.loginRegister')}
          </button>
          <div className="mt-4 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-wide text-ink-300">
            <span className="h-px flex-1 bg-ink-200" /> {t('info.orFillBelow')} <span className="h-px flex-1 bg-ink-200" />
          </div>
        </div>
      )}
      <div className="gsm-card space-y-5 p-5 sm:p-6">
        <div>
          <h2 className="mb-1 font-display text-sm font-bold text-ink-900">{t('info.title')}</h2>
          <p className="text-[12.5px] text-ink-500">{t('info.helper')}</p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t('info.firstName')} required={!isAzienda} value={info.name} onChange={(e) => set('name', e.target.value)} error={err('name')} autoComplete="given-name" />
          <Field label={t('info.lastName')} required={!isAzienda} value={info.lastname} onChange={(e) => set('lastname', e.target.value)} error={err('lastname')} autoComplete="family-name" />
          <Field label={t('info.email')} required type="email" value={info.email} onChange={(e) => set('email', e.target.value)} error={err('email')} autoComplete="email" />
          <Field
            label={t('info.emailConfirm')}
            required
            type="email"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            onPaste={(e) => e.preventDefault()}
            onDrop={(e) => e.preventDefault()}
            onContextMenu={(e) => e.preventDefault()}
            autoComplete="off"
            error={touched && errors.emailConfirm ? t('info.emailMismatch') : undefined}
          />
          <Field label={t('info.phone')} required value={info.phoneNumber} onChange={(e) => set('phoneNumber', e.target.value)} error={err('phoneNumber')} autoComplete="tel" />
          <Field label={t('info.plate')} value={info.licensePlate} onChange={(e) => set('licensePlate', e.target.value.toUpperCase())} className="sm:col-span-1" />
          <div>
            <label className="gsm-label">{t('info.country')}</label>
            <Select
              value={info.country || 'IT'}
              options={COUNTRY_OPTIONS}
              onChange={(v) => setBookingInfo({ country: v, businessCountry: v })}
              searchable
              ariaLabel={t('info.country')}
              placeholder={t('info.country')}
            />
          </div>
        </div>

        <div className="border-t border-ink-100 pt-2">
          <Checkbox
            checked={info.privacy01 === 'true'}
            onChange={(v) => set('privacy01', v ? 'true' : '')}
            label={
              <span>
                {t('info.privacy')} <span className="text-brand-600">*</span>
              </span>
            }
          />
          {touched && errors.privacy01 && <p className="ml-8 text-xs font-medium text-brand-600">{t('common.required')}</p>}
          <Checkbox checked={info.privacy02 === 'true'} onChange={(v) => set('privacy02', v ? 'true' : '')} label={t('info.marketing')} />
          <Checkbox checked={info.privacy03 === 'true'} onChange={(v) => set('privacy03', v ? 'true' : '')} label={t('info.profiling')} />
        </div>
      </div>

      {/* Dati di fatturazione — TEMPORARILY HIDDEN via BILLING_ENABLED (set VITE_BILLING_ENABLED=true
          to restore). Privato or Azienda when shown. */}
      {BILLING_ENABLED && (
        <section className="gsm-card mt-4 p-5 sm:p-6">
          <h2 className="mb-1 font-display text-sm font-bold text-ink-900">{t('info.billingTitle')}</h2>
          <p className="mb-4 text-[12.5px] text-ink-500">{t('info.billingHint')}</p>
          <BillingFields
            value={info}
            set={(k, v) => {
              if (k === 'businessEmail') billingEmailEdited.current = true
              setBookingInfo({ [k]: v } as Partial<BookingInfo>)
            }}
            touched={touched}
            requiredLabel={t('common.required')}
          />
        </section>
      )}
    </StepShell>
  )
}
